import clsx from 'clsx';
import { Activity, BarChart3, LogOut, UserCircle, Users } from 'lucide-react';
import type { PropsWithChildren } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { Button } from '@doger/ui/components/ui/button';
import { SidebarFooter } from '@doger/ui/components/ui/sidebar';
import { useJWT } from '@doger/core/modules/auth';
import { useSignOut } from '@doger/core/modules/auth';
import { LogoDark } from '@doger/core/shared/ui/logo/logo.component';

import styles from './layout.module.scss';

const navItems = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: BarChart3,
  },

  {
    label: 'Users',
    href: '/users',
    icon: Users,
  },

  {
    label: 'System Metrics',
    href: '/metrics',
    icon: Activity,
  },
];

const Layout = memo(({ children }: PropsWithChildren) => (
  <div className={styles.root}>
    <aside className={styles.sidebar}>
      <div className={styles.sidebarLogo}>
        <div className={styles.sidebarLogo__header}>
          <div className={styles.sidebarLogo__icon}>
            <LogoDark width={50} height={50} style={{ marginBottom: -17.5 }} />
          </div>
          <div className={styles.sidebarLogo__text}>
            <h1 className={styles.sidebarLogo__title}>DOGER</h1>

            <p className={styles.sidebarLogo__subtitle}>Admin Panel</p>
          </div>
        </div>
      </div>

      <div className={styles.navigation}>
        {navItems.map((item) => {
          const IconComponent = item.icon;

          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                clsx(styles.navigation__item, {
                  [styles.navigation__itemActive]: isActive,
                })
              }
            >
              <IconComponent size={20} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </div>

      <AppSidebarFooter />
    </aside>

    <main className={styles.content}>{children}</main>
  </div>
));

const AppSidebarFooter = memo(() => {
  const jwt = useJWT();
  const { t } = useTranslation();
  const signOut = useSignOut();

  return (
    <SidebarFooter className={styles.footer}>
      <div className={styles.footerProfileContainer}>
        <UserCircle />

        <span className={styles.footerProfile}>
          <span className={styles.footerProfileName}>
            {capitalizeFirstLetter(jwt!.role)}
          </span>
          <span className={styles.footerProfileEmail}>{jwt!.email}</span>
        </span>
      </div>

      <Button
        className={styles.footerButton}
        onClick={() => signOut.mutate()}
        variant='contained'
        size='lg'
        palette='ghost'
      >
        <LogOut />
        {t('auth.userProfile.signOut', { ns: 'core' })}
      </Button>
    </SidebarFooter>
  );
});

function capitalizeFirstLetter(string: string) {
  const lowerCaseString = string.toLowerCase();

  return lowerCaseString.charAt(0).toUpperCase() + lowerCaseString.slice(1);
}

export { Layout };
